/**
 * Sprint Redux Slice
 * Comprehensive sprint management with monday.com features
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import sprintService from '../entities/sprint/api/sprintService';
import {
  Sprint,
  SprintGoal,
  SprintState,
  CreateSprintDTO,
  UpdateSprintDTO,
} from '../entities/sprint/model/types';

const initialState: SprintState = {
  sprints: [],
  currentSprint: null,
  activeSprint: null,
  sprintOverview: null,
  burndownData: null,
  velocityData: null,
  plannedVsUnplannedData: null,
  insights: null,
  kanbanBoard: null,
  automations: [],
  loading: false,
  error: null,
};

// ============================================
// SPRINT CRUD OPERATIONS
// ============================================

/**
 * Fetch all sprints for a board
 */
export const fetchSprints = createAsyncThunk(
  'sprint/fetchSprints',
  async (boardId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getSprintsForBoard(boardId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sprints');
    }
  }
);

/**
 * Fetch a single sprint by ID
 */
export const fetchSprint = createAsyncThunk(
  'sprint/fetchSprint',
  async (sprintId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getSprint(sprintId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sprint');
    }
  }
);

/**
 * Fetch active sprint for a board
 */
export const fetchActiveSprint = createAsyncThunk(
  'sprint/fetchActiveSprint',
  async (boardId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getActiveSprint(boardId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch active sprint');
    }
  }
);

/**
 * Create a new sprint
 */
export const createSprint = createAsyncThunk(
  'sprint/createSprint',
  async (data: CreateSprintDTO, { rejectWithValue }) => {
    try {
      return await sprintService.createSprint(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create sprint');
    }
  }
);

/**
 * Update an existing sprint
 */
export const updateSprint = createAsyncThunk(
  'sprint/updateSprint',
  async ({ sprintId, data }: { sprintId: string; data: UpdateSprintDTO }, { rejectWithValue }) => {
    try {
      return await sprintService.updateSprint(sprintId, data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update sprint');
    }
  }
);

/**
 * Delete a sprint
 */
export const deleteSprint = createAsyncThunk(
  'sprint/deleteSprint',
  async (sprintId: string, { rejectWithValue }) => {
    try {
      await sprintService.deleteSprint(sprintId);
      return sprintId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete sprint');
    }
  }
);

// ============================================
// SPRINT LIFECYCLE
// ============================================

/**
 * Start a sprint
 */
export const startSprint = createAsyncThunk(
  'sprint/startSprint',
  async (sprintId: string, { rejectWithValue }) => {
    try {
      return await sprintService.startSprint(sprintId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to start sprint');
    }
  }
);

/**
 * Complete a sprint
 */
export const completeSprint = createAsyncThunk(
  'sprint/completeSprint',
  async (
    { sprintId, incompleteTaskAction }: { sprintId: string; incompleteTaskAction?: string },
    { rejectWithValue }
  ) => {
    try {
      const resolution =
        incompleteTaskAction === 'move-to-next-sprint' || incompleteTaskAction === 'move_to_next_sprint'
          ? 'move_to_next_sprint'
          : 'move_to_backlog';
      return await sprintService.completeSprint(sprintId, { resolution } as any);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to complete sprint');
    }
  }
);

/**
 * Reset a sprint
 */
export const resetSprint = createAsyncThunk(
  'sprint/resetSprint',
  async (sprintId: string, { rejectWithValue }) => {
    try {
      return await sprintService.resetSprint(sprintId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reset sprint');
    }
  }
);

// ============================================
// SPRINT OVERVIEW
// ============================================

/**
 * Fetch sprint overview
 */
export const fetchSprintOverview = createAsyncThunk(
  'sprint/fetchSprintOverview',
  async (sprintId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getSprintOverview(sprintId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sprint overview');
    }
  }
);

// ============================================
// SPRINT GOALS
// ============================================

/**
 * Add a goal to sprint
 */
export const addSprintGoal = createAsyncThunk(
  'sprint/addSprintGoal',
  async ({ sprintId, goal }: { sprintId: string; goal: Omit<SprintGoal, 'id' | 'sprintId' | 'createdAt'> }, { rejectWithValue }) => {
    try {
      return await sprintService.addSprintGoal(sprintId, goal.description);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add sprint goal');
    }
  }
);

/**
 * Update sprint goal
 */
export const updateSprintGoal = createAsyncThunk(
  'sprint/updateSprintGoal',
  async ({ goalId, updates }: { goalId: string; updates: Partial<SprintGoal> }, { rejectWithValue }) => {
    try {
      return await sprintService.updateSprintGoal(goalId, updates);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update sprint goal');
    }
  }
);

// ============================================
// SPRINT TASKS
// ============================================

/**
 * Fetch sprint tasks
 */
export const fetchSprintTasks = createAsyncThunk(
  'sprint/fetchSprintTasks',
  async (sprintId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getSprintTasks(sprintId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sprint tasks');
    }
  }
);

/**
 * Add task to sprint
 */
export const addTaskToSprint = createAsyncThunk(
  'sprint/addTaskToSprint',
  async ({ sprintId, taskId }: { sprintId: string; taskId: string }, { rejectWithValue }) => {
    try {
      await sprintService.moveTasksToSprint(sprintId, { taskIds: [taskId], targetSprintId: sprintId });
      return taskId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add task to sprint');
    }
  }
);

/**
 * Remove task from sprint
 */
export const removeTaskFromSprint = createAsyncThunk(
  'sprint/removeTaskFromSprint',
  async ({ sprintId, taskId }: { sprintId: string; taskId: string }, { rejectWithValue }) => {
    try {
      await sprintService.removeTasksFromSprint(sprintId, [taskId]);
      return taskId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove task from sprint');
    }
  }
);

// ============================================
// CHARTS & INSIGHTS
// ============================================

/**
 * Fetch burndown chart data
 */
export const fetchBurndownChart = createAsyncThunk(
  'sprint/fetchBurndownChart',
  async (sprintId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getBurndownChart(sprintId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch burndown chart');
    }
  }
);

/**
 * Fetch velocity data
 */
export const fetchVelocityData = createAsyncThunk(
  'sprint/fetchVelocityData',
  async (boardId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getVelocityChart(boardId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch velocity data');
    }
  }
);

/**
 * Fetch planned vs unplanned data
 */
export const fetchPlannedVsUnplannedData = createAsyncThunk(
  'sprint/fetchPlannedVsUnplannedData',
  async (boardId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getPlannedVsUnplannedChart(boardId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch planned vs unplanned data');
    }
  }
);

/**
 * Fetch sprint insights
 */
export const fetchSprintInsights = createAsyncThunk(
  'sprint/fetchSprintInsights',
  async (sprintId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getSprintInsights(sprintId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sprint insights');
    }
  }
);

// ============================================
// KANBAN BOARD
// ============================================

/**
 * Fetch sprint kanban board
 */
export const fetchSprintKanban = createAsyncThunk(
  'sprint/fetchSprintKanban',
  async (sprintId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getSprintKanban(sprintId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sprint kanban');
    }
  }
);

/**
 * Move a task between columns and/or reorder it within a column.
 * Optimistic-concurrency aware: rejects with the server's 409 body
 * (including its `currentTask`) when `expectedVersion` is stale, so the
 * caller can reconcile instead of leaving the UI stuck on a lost update.
 */
export const moveTask = createAsyncThunk(
  'sprint/moveTask',
  async (
    {
      taskId,
      expectedVersion,
      status,
      position,
    }: {
      sprintId: string;
      taskId: string;
      expectedVersion: number;
      status?: string;
      position?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      return await sprintService.moveTask(taskId, { expectedVersion, status, position });
    } catch (error: any) {
      if (error.response?.status === 409) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue({ message: error.response?.data?.message || 'Failed to move task' });
    }
  }
);

// ============================================
// AUTOMATIONS
// ============================================

/**
 * Fetch sprint automations
 */
export const fetchSprintAutomations = createAsyncThunk(
  'sprint/fetchSprintAutomations',
  async (sprintId: string, { rejectWithValue }) => {
    try {
      return await sprintService.getSprintAutomations(sprintId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sprint automations');
    }
  }
);

// ============================================
// SLICE
// ============================================

const sprintSlice = createSlice({
  name: 'sprint',
  initialState,
  reducers: {
    clearCurrentSprint: (state) => {
      state.currentSprint = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentSprint: (state, action: PayloadAction<Sprint>) => {
      state.currentSprint = action.payload;
    },
    clearSprintData: (state) => {
      state.sprintOverview = null;
      state.burndownData = null;
      state.velocityData = null;
      state.plannedVsUnplannedData = null;
      state.insights = null;
      state.kanbanBoard = null;
      state.automations = [];
    },
    updateKanbanBoardOptimistic: (state, action: PayloadAction<any>) => {
      state.kanbanBoard = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch sprints
    builder
      .addCase(fetchSprints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSprints.fulfilled, (state, action) => {
        state.loading = false;
        state.sprints = action.payload;
      })
      .addCase(fetchSprints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch sprint
    builder
      .addCase(fetchSprint.fulfilled, (state, action) => {
        state.currentSprint = action.payload;
      });

    // Fetch active sprint
    builder
      .addCase(fetchActiveSprint.fulfilled, (state, action) => {
        state.activeSprint = action.payload;
      });

    // Create sprint
    builder
      .addCase(createSprint.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSprint.fulfilled, (state, action) => {
        state.loading = false;
        state.sprints.push(action.payload);
      })
      .addCase(createSprint.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update sprint
    builder
      .addCase(updateSprint.fulfilled, (state, action) => {
        const index = state.sprints.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.sprints[index] = action.payload;
        }
        if (state.currentSprint?.id === action.payload.id) {
          state.currentSprint = action.payload;
        }
      });

    // Delete sprint
    builder
      .addCase(deleteSprint.fulfilled, (state, action) => {
        state.sprints = state.sprints.filter((s) => s.id !== action.payload);
        if (state.currentSprint?.id === action.payload) {
          state.currentSprint = null;
        }
      });

    // Start sprint
    builder
      .addCase(startSprint.fulfilled, (state, action) => {
        const index = state.sprints.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.sprints[index] = action.payload;
        }
        state.activeSprint = action.payload;
        if (state.currentSprint?.id === action.payload.id) {
          state.currentSprint = action.payload;
        }
      });

    // Complete sprint
    builder
      .addCase(completeSprint.fulfilled, (state, action) => {
        const index = state.sprints.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.sprints[index] = action.payload;
        }
        if (state.activeSprint?.id === action.payload.id) {
          state.activeSprint = null;
        }
      });

    // Reset sprint
    builder
      .addCase(resetSprint.fulfilled, (state, action) => {
        const index = state.sprints.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.sprints[index] = action.payload;
        }
      });

    // Fetch sprint overview
    builder
      .addCase(fetchSprintOverview.fulfilled, (state, action) => {
        state.sprintOverview = action.payload;
      });

    // Fetch burndown chart
    builder
      .addCase(fetchBurndownChart.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBurndownChart.fulfilled, (state, action) => {
        state.loading = false;
        state.burndownData = Array.isArray(action.payload) ? action.payload : (action.payload?.dataPoints || []);
      })
      .addCase(fetchBurndownChart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch velocity data
    builder
      .addCase(fetchVelocityData.fulfilled, (state, action) => {
        state.velocityData = action.payload;
      });

    // Fetch planned vs unplanned data
    builder
      .addCase(fetchPlannedVsUnplannedData.fulfilled, (state, action) => {
        state.plannedVsUnplannedData = action.payload;
      });

    // Fetch insights
    builder
      .addCase(fetchSprintInsights.fulfilled, (state, action) => {
        state.insights = action.payload;
      });

    // Fetch kanban board
    builder
      .addCase(fetchSprintKanban.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSprintKanban.fulfilled, (state, action) => {
        state.loading = false;
        state.kanbanBoard = action.payload;
      })
      .addCase(fetchSprintKanban.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch automations
    builder
      .addCase(fetchSprintAutomations.fulfilled, (state, action) => {
        state.automations = action.payload;
      });

    // Move task (drag-drop / reorder)
    builder
      .addCase(moveTask.rejected, (state, action) => {
        state.error =
          (action.payload as any)?.message || 'Failed to move task';
      });
  },
});

export const { clearCurrentSprint, clearError, setCurrentSprint, clearSprintData, updateKanbanBoardOptimistic } =
  sprintSlice.actions;

export default sprintSlice.reducer;
